import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Download, ClipboardList, LogOut, Loader2, ShoppingBag, PlayCircle, Award, CalendarClock, MessageSquare, FileText, MapPin, Users, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import BusinessWorkforce from "@/components/dashboards/BusinessWorkforce";
import CaregiverHub from "@/components/dashboards/CaregiverHub";
import Messages from "@/components/Messages";
import DocumentVault from "@/components/DocumentVault";
import { Link as RouterLink } from "react-router-dom";

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [requests, setRequests] = useState([]);
  const [courses, setCourses] = useState([]);
  const [portal, setPortal] = useState({ shifts: [], leads: [] });
  const [openWith, setOpenWith] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (user.account_type === "business" || user.account_type === "caregiver") return;
    api.get("/me/purchases").then((r) => setPurchases(r.data)).catch(() => {});
    api.get("/me/care-requests").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/me/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/portal/family").then((r) => setPortal(r.data)).catch(() => {});
  }, [user, loading, navigate]);

  if (loading || !user) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  const Wrapper = ({ children }) => (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex justify-end mb-4">
        <Button variant="outline" size="sm" data-testid="dashboard-logout-button" onClick={() => { logout(); navigate("/"); }} className="gap-2"><LogOut className="w-4 h-4" /> Sign out</Button>
      </div>
      {children}
    </div>
  );

  if (user.account_type === "business") return <Wrapper><BusinessWorkforce user={user} /></Wrapper>;
  if (user.account_type === "caregiver") return <Wrapper><CaregiverHub user={user} /></Wrapper>;

  return (
    <Wrapper>
      <h1 className="font-display text-3xl lg:text-4xl font-bold text-blue-950">My T5 Family</h1>
      <p className="text-slate-500 mt-1 mb-8">{user.email}</p>
      <Tabs defaultValue="care">
        <TabsList data-testid="dashboard-tabs" className="flex-wrap h-auto">
          <TabsTrigger value="care" data-testid="tab-care">My Care</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents-portal">Documents</TabsTrigger>
          <TabsTrigger value="courses" data-testid="tab-courses">My Courses</TabsTrigger>
          <TabsTrigger value="downloads" data-testid="tab-downloads">Downloads</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">Care Requests</TabsTrigger>
        </TabsList>

        <TabsContent value="care" className="mt-6" data-testid="portal-care">
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <RouterLink to="/concierge" className="bg-blue-950 text-white rounded-2xl p-5 hover:bg-blue-900 transition-colors" data-testid="portal-navigator">
              <Sparkles className="w-6 h-6 text-teal-400" /><h3 className="font-display font-semibold mt-2">AI Care Navigator</h3><p className="text-sm text-blue-200/70">Ask for help or start a new care request anytime.</p>
            </RouterLink>
            <RouterLink to="/marketplace" className="bg-white border border-blue-900/5 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow" data-testid="portal-find-care">
              <Users className="w-6 h-6 text-teal-600" /><h3 className="font-display font-semibold text-blue-950 mt-2">Find caregivers</h3><p className="text-sm text-slate-500">Browse verified caregivers in T5 Connect.</p>
            </RouterLink>
          </div>
          <h2 className="font-display text-lg font-bold text-blue-950 mb-3">Your care team & schedule</h2>
          {portal.shifts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-blue-900/5"><CalendarClock className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-slate-500">No scheduled care yet. Start with the AI Care Navigator.</p></div>
          ) : (
            <div className="space-y-3">
              {portal.shifts.map((s) => (
                <div key={s.id} className="bg-white rounded-2xl p-4 border border-blue-900/5 shadow-sm flex items-center justify-between gap-3 flex-wrap" data-testid={`fam-shift-${s.id}`}>
                  <div>
                    <p className="font-semibold text-blue-950">{s.caregiver_name || "Caregiver being matched"}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-3 flex-wrap mt-1"><span>{s.date} · {s.start}–{s.end}</span>{s.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {s.location}</span>}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{s.services}</p>
                  </div>
                  {s.caregiver_email && <Button size="sm" variant="outline" className="gap-1.5" data-testid={`fam-message-${s.id}`} onClick={() => { setOpenWith({ email: s.caregiver_email, name: s.caregiver_name }); document.querySelector('[data-testid="tab-messages"]')?.click(); }}><MessageSquare className="w-4 h-4" /> Message caregiver</Button>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="messages" className="mt-6"><Messages openWith={openWith} /></TabsContent>

        <TabsContent value="documents" className="mt-6">
          <div className="bg-white rounded-2xl p-6 border border-blue-900/5 shadow-sm"><h3 className="font-display font-semibold text-blue-950 mb-2 flex items-center gap-2"><FileText className="w-5 h-5 text-teal-600" /> Care documents</h3><p className="text-sm text-slate-500 mb-4">Store care plans, insurance and medical documents securely.</p><DocumentVault /></div>
        </TabsContent>

        <TabsContent value="courses" className="mt-6">
          {courses.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-blue-900/5"><PlayCircle className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-slate-500">No courses yet.</p><Button asChild className="mt-4 bg-blue-900 text-white"><Link to="/store">Browse courses</Link></Button></div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {courses.map((c) => {
                const pct = c.total_lessons ? Math.round((c.completed_lessons.length / c.total_lessons) * 100) : 0;
                return (
                  <div key={c.course_id} data-testid={`course-${c.course_id}`} className="bg-white rounded-2xl p-4 border border-blue-900/5 shadow-sm flex gap-4">
                    <img src={c.image} alt={c.name} className="w-20 h-20 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-blue-950 line-clamp-2">{c.name}</p>
                      <div className="mt-2 h-2 rounded-full bg-blue-100 overflow-hidden"><div className="h-full bg-teal-600" style={{ width: `${pct}%` }} /></div>
                      <div className="flex gap-2 mt-2">
                        <Button asChild size="sm" className="bg-blue-900 text-white gap-1"><Link to={`/learn/${c.course_id}`} data-testid={`continue-${c.course_id}`}><PlayCircle className="w-4 h-4" /> {pct > 0 ? "Continue" : "Start"}</Link></Button>
                        {c.quiz_passed && <Button asChild size="sm" variant="outline" className="gap-1 border-amber-500 text-amber-700"><Link to={`/certificate/${c.course_id}`} data-testid={`cert-${c.course_id}`}><Award className="w-4 h-4" /> Cert</Link></Button>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="downloads" className="mt-6">
          {purchases.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-blue-900/5"><ShoppingBag className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-slate-500">No purchases yet.</p><Button asChild className="mt-4 bg-blue-900 text-white"><Link to="/store">Browse the store</Link></Button></div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {purchases.map((p) => (
                <div key={p.product_id} data-testid={`purchase-${p.product_id}`} className="flex gap-4 bg-white rounded-2xl p-4 border border-blue-900/5 shadow-sm">
                  <img src={p.image} alt={p.name} className="w-20 h-20 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0"><p className="font-medium text-sm text-blue-950 line-clamp-2">{p.name}</p><p className="text-xs text-slate-400 mt-1 capitalize">{p.type}</p><Button asChild size="sm" className="mt-2 bg-teal-600 hover:bg-teal-700 text-white gap-1.5"><a href={p.download_url} target="_blank" rel="noreferrer" data-testid={`download-${p.product_id}`}><Download className="w-4 h-4" /> Download</a></Button></div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="requests" className="mt-6">
          {requests.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-blue-900/5"><ClipboardList className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-slate-500">No care requests yet.</p><Button asChild className="mt-4 bg-blue-900 text-white"><Link to="/request-care">Request care</Link></Button></div>
          ) : (
            <div className="space-y-3">
              {requests.map((r) => (
                <div key={r.id} data-testid={`request-${r.id}`} className="flex items-center justify-between bg-white rounded-xl p-4 border border-blue-900/5"><div><p className="font-medium text-blue-950">{r.care_type}</p><p className="text-sm text-slate-500">{r.message || "No additional notes"}</p></div><span className="text-xs font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 px-3 py-1 rounded-full">{r.status}</span></div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </Wrapper>
  );
}
